type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
};

declare const window: WindowWithDataLayer;

window.dataLayer = window.dataLayer || [];

export const pageview = (url: string) => {
  window.dataLayer.push({
    event: 'page_view',
    page: url
  });
};

interface EventProps {
  action: string;
  category: string;
  label: string;
  value?: string;
}
export const event = ({ action, category, label, value }: EventProps) => {
  window.dataLayer.push({
    event: 'eventTracking',
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventValue: value
  });
};

interface EventTrackingClickProps {
  category: string;
  label: string;
}
export const eventTracking = {
  click: ({ category, label }: EventTrackingClickProps) => {
    event({
      action: 'Clique',
      category,
      label
    });
  }
};

interface CusTomEventTrackingClickProps {
  event: string;
  props?: Record<string, any>;
}

export const customEventTracking = {
  send: ({ event, props }: CusTomEventTrackingClickProps) => {
    window.dataLayer.push({
      event: event,
      ...props
    });
  }
};

interface DynamicCusTomEventTrackingClickProps {
  dynamicEventName: string;
}

export const dynamicCustomEventTracking = {
  send: ({ dynamicEventName }: DynamicCusTomEventTrackingClickProps) => {
    window.dataLayer.push({
      event: 'dynamicEventTracking',
      dynamicEventName
    });
  }
};

type ScheduleAnalyticsData = {
  scheduleDateStart: string;
  scheduleCalendarId: string;
  scheduleCalendarAlias: string;
  scheduleId: string;
  journeyId: string;
  scheduleSelectionFlow: string;
  scheduleFlow: string;
  featuredSchedulesDisplay: string;
  featuredSchedulesSlot: string[];
  featuredSchedulesHasSelectSomeSlot: string;
  rescheduleChoice: string;
  scheduleOldDoctorId: string;
  scheduleNewDoctorId: string;
  scheduleProductId: string;
};

class ScheduleAnalyticsTracker {
  private scheduleSelectionFlow: ScheduleAnalyticsData['scheduleSelectionFlow'] = 'default';
  private scheduleFlow: ScheduleAnalyticsData['scheduleFlow'] = 'unknown';
  private scheduleDateStart: ScheduleAnalyticsData['scheduleDateStart'] = '';
  private scheduleCalendarId: ScheduleAnalyticsData['scheduleCalendarId'] = '';
  private scheduleCalendarAlias: ScheduleAnalyticsData['scheduleCalendarAlias'] = '';
  private scheduleId: ScheduleAnalyticsData['scheduleId'] = '';
  private journeyId: ScheduleAnalyticsData['journeyId'] = '';
  private featuredSchedulesDisplay: ScheduleAnalyticsData['featuredSchedulesDisplay'] = 'no';
  private featuredSchedulesSlot: ScheduleAnalyticsData['featuredSchedulesSlot'] = [];
  private featuredSchedulesHasSelectSomeSlot: ScheduleAnalyticsData['featuredSchedulesHasSelectSomeSlot'] = 'no';
  private rescheduleChoice: ScheduleAnalyticsData['rescheduleChoice'] = '';
  private scheduleOldDoctorId: ScheduleAnalyticsData['scheduleOldDoctorId'] = '';
  private scheduleNewDoctorId: ScheduleAnalyticsData['scheduleNewDoctorId'] = '';
  private scheduleProductId: ScheduleAnalyticsData['scheduleProductId'] = '';
  private static instance: ScheduleAnalyticsTracker;

  constructor() {
    if (!ScheduleAnalyticsTracker.instance) {
      ScheduleAnalyticsTracker.instance = this;
    }
    return ScheduleAnalyticsTracker.instance;
  }

  setProperties(data: Partial<ScheduleAnalyticsData>) {
    if (data.scheduleSelectionFlow) this.scheduleSelectionFlow = data.scheduleSelectionFlow;
    if (data.scheduleFlow) this.scheduleFlow = data.scheduleFlow;
    if (data.scheduleDateStart) this.scheduleDateStart = data.scheduleDateStart;
    if (data.scheduleCalendarId) this.scheduleCalendarId = data.scheduleCalendarId;
    if (data.scheduleCalendarAlias) this.scheduleCalendarAlias = data.scheduleCalendarAlias;
    if (data.scheduleId) this.scheduleId = data.scheduleId;
    if (data.journeyId) this.journeyId = data.journeyId;
    if (data.featuredSchedulesSlot) this.featuredSchedulesSlot = data.featuredSchedulesSlot;
    if (data.featuredSchedulesDisplay) this.featuredSchedulesDisplay = data.featuredSchedulesDisplay;
    if (data.featuredSchedulesHasSelectSomeSlot)
      this.featuredSchedulesHasSelectSomeSlot = data.featuredSchedulesHasSelectSomeSlot;
    if (data.rescheduleChoice !== undefined) this.rescheduleChoice = data.rescheduleChoice;
    if (data.scheduleOldDoctorId !== undefined) this.scheduleOldDoctorId = data.scheduleOldDoctorId;
    if (data.scheduleNewDoctorId !== undefined) this.scheduleNewDoctorId = data.scheduleNewDoctorId;
    if (data.scheduleProductId !== undefined) this.scheduleProductId = data.scheduleProductId;
    return this;
  }

  sendGAEvent() {
    if (this.scheduleDateStart) {
      this.featuredSchedulesHasSelectSomeSlot = this.featuredSchedulesSlot.some(item =>
        item.includes(this.scheduleDateStart)
      )
        ? 'yes'
        : 'no';
    }
    customEventTracking.send({
      event: 'trigger_schedule_finished_mc',
      props: this.getData()
    });
    this.clear();
  }

  clear() {
    this.scheduleSelectionFlow = 'default';
    this.scheduleFlow = 'unknown';
    this.scheduleDateStart = '';
    this.scheduleCalendarId = '';
    this.scheduleCalendarAlias = '';
    this.scheduleId = '';
    this.journeyId = '';
    this.featuredSchedulesDisplay = 'no';
    this.featuredSchedulesSlot = [];
    this.featuredSchedulesHasSelectSomeSlot = 'no';
    this.rescheduleChoice = '';
    this.scheduleOldDoctorId = '';
    this.scheduleNewDoctorId = '';
    this.scheduleProductId = '';
  }

  getData(): ScheduleAnalyticsData {
    return {
      scheduleDateStart: this.scheduleDateStart,
      scheduleCalendarId: this.scheduleCalendarId,
      scheduleCalendarAlias: this.scheduleCalendarAlias,
      scheduleId: this.scheduleId,
      scheduleSelectionFlow: this.scheduleSelectionFlow,
      scheduleFlow: this.scheduleFlow,
      journeyId: this.journeyId,
      featuredSchedulesDisplay: this.featuredSchedulesDisplay,
      featuredSchedulesSlot: this.featuredSchedulesSlot,
      featuredSchedulesHasSelectSomeSlot: this.featuredSchedulesHasSelectSomeSlot,
      rescheduleChoice: this.rescheduleChoice,
      scheduleOldDoctorId: this.scheduleOldDoctorId,
      scheduleNewDoctorId: this.scheduleNewDoctorId,
      scheduleProductId: this.scheduleProductId
    };
  }
}

export const ScheduleAnalyticsTrackerInstance = new ScheduleAnalyticsTracker();
